import type { BackendTask, BackendTaskStatus } from '@/lib/api';
import { computeDeltaSeconds } from './format';

/**
 * Status canônico (espelha DB tasks_status_check).
 * UI usa esse vocabulário; tradução pra label PT é só na renderização.
 */
export type TaskStatus = BackendTaskStatus;

export const TASK_STATUSES: readonly TaskStatus[] = [
  'pending',
  'running',
  'done',
  'failed',
  'retry',
  'cancelled',
  'paused',
] as const;

export const STATUS_LABEL: Record<TaskStatus, string> = {
  pending: 'Pendente',
  running: 'Em execução',
  done: 'Concluído',
  failed: 'Falhou',
  retry: 'Retry',
  cancelled: 'Cancelado',
  paused: 'Pausado',
};

/** Cor token da borda lateral do card por status. */
export const STATUS_BORDER_TOKEN: Record<TaskStatus, string> = {
  pending: 'border-l-raios-warning',
  running: 'border-l-raios-accent',
  done: 'border-l-raios-success',
  failed: 'border-l-raios-danger',
  retry: 'border-l-raios-warning',
  cancelled: 'border-l-raios-muted',
  paused: 'border-l-raios-muted',
};

/** Mapeia status DB → variant do StatusBadge do DS. */
export const STATUS_BADGE_VARIANT: Record<TaskStatus, 'pending' | 'running' | 'success' | 'error' | 'warning' | 'cancelled' | 'idle'> = {
  pending: 'pending',
  running: 'running',
  done: 'success',
  failed: 'error',
  retry: 'warning',
  cancelled: 'cancelled',
  paused: 'idle',
};

/** Status agrupados em "Issues" (visualmente containerizados). */
export const ISSUE_STATUSES: readonly TaskStatus[] = ['failed', 'retry', 'cancelled'] as const;

/** Status principais (sempre visíveis, lado a lado). */
export const PRIMARY_STATUSES: readonly TaskStatus[] = ['pending', 'running', 'done'] as const;

/* ─── Filtros tipo por handler ───────────────────────────────────────────── */

/**
 * Cada entry vira um dropdown SEPARADO na toolbar quando há tasks daquele
 * handler em cache. Echo é excluído (handler de teste).
 */
export interface HandlerTypeMeta {
  handler: string;
  label: string;
  /** Chave usada no querystring + state. */
  slug: 'agent' | 'skill' | 'tool' | 'op' | 'pipeline' | 'wizard';
}

export const HANDLER_TYPE_FILTERS: readonly HandlerTypeMeta[] = [
  { handler: '@agent-runner', label: 'Agente', slug: 'agent' },
  { handler: '@skill-runner', label: 'Skill', slug: 'skill' },
  { handler: '@tool-runner', label: 'Tool', slug: 'tool' },
  { handler: '@op-runner', label: 'Operação', slug: 'op' },
  { handler: '@pipeline-runner', label: 'Pipeline', slug: 'pipeline' },
  { handler: '@wizard-runner', label: 'Wizard', slug: 'wizard' },
] as const;

export const EXCLUDED_HANDLERS_FROM_FILTERS: ReadonlySet<string> = new Set(['@echo']);

export function findHandlerTypeMetaBySlug(slug: string): HandlerTypeMeta | undefined {
  return HANDLER_TYPE_FILTERS.find((h) => h.slug === slug);
}

export function findHandlerTypeMetaByHandler(handler: string): HandlerTypeMeta | undefined {
  return HANDLER_TYPE_FILTERS.find((h) => h.handler === handler);
}

/**
 * Tipo enxuto pro card/board. Subset de BackendTask sem JSONB pesado.
 * Modal carrega os campos pesados via getTask(id) sob demanda.
 */
export interface TaskUI {
  id: string;
  status: TaskStatus;
  handler: string;
  target_slug: string | null;
  title: string;
  demand_id: string | null;
  pipeline_run_id: string | null;
  pipeline_step_id: string | null;
  session_id: string | null;
  agent_id: string | null;
  /**
   * P1.13 F7 — Quando preenchido, sinaliza task derivada (ex: sub-task de skill).
   * Combinado com `is_orphan`, identifica órfãs canônicas (FK NULL pós-B1 #415).
   */
  parent_task_id: string | null;
  /**
   * P1.13 F7 — Computado client-side em backendToTaskUI:
   *   parent_task_id != null AND (demand_id == null OR pipeline_run_id == null)
   *
   * Tasks "órfãs" são sub-tasks de skill que não receberam herança de FKs do
   * pai. Backend B1 (#415) garante propagação automática; órfãs remanescentes
   * são legacy de pré-merge ou bug residual a investigar.
   */
  is_orphan: boolean;
  estimated_duration_seconds: number | null;
  /**
   * G4 F1 (D-G4-F): renamed from duration_seconds. Backend G3 PR-P1 dropou
   * coluna tasks.duration_seconds → padronizado em tasks.duration_ms BIGINT.
   * Front consome o ms canonical e converte pra segundos no display.
   */
  duration_ms: number | null;
  delta_estimated_actual_seconds: number | null;
  retry_count: number;
  max_retries: number;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  urgency_level: string | null;
  estimated_size: string | null;
  domain: string | null;
}

/** Adapta BackendTask para TaskUI. Title fallback hierárquico. */
export function backendToTaskUI(t: BackendTask): TaskUI {
  const input = t.input ?? {};
  const scope = (input.scope as string | undefined)?.trim();
  const title =
    (t.title ?? '').trim() ||
    scope ||
    (t.target_slug ?? '').trim() ||
    t.event_type ||
    '(sem título)';

  // P1.13 F7 — Detecção de task órfã: tem parent (sub-task) MAS perdeu pelo
  // menos uma FK canônica que o pai deveria ter passado adiante.
  const parentTaskId = t.parent_task_id ?? null;
  const demandId = t.demand_id ?? null;
  const pipelineRunId = t.pipeline_run_id ?? null;
  const isOrphan = parentTaskId !== null && (demandId === null || pipelineRunId === null);

  return {
    id: t.id,
    status: t.status,
    handler: t.handler,
    target_slug: t.target_slug ?? null,
    title,
    demand_id: demandId,
    pipeline_run_id: pipelineRunId,
    pipeline_step_id: t.pipeline_step_id ?? null,
    session_id: t.session_id ?? null,
    agent_id: t.agent_id ?? null,
    parent_task_id: parentTaskId,
    is_orphan: isOrphan,
    estimated_duration_seconds: t.estimated_duration_seconds ?? null,
    // G4 F1 (D-G4-F): consome tasks.duration_ms canonical do backend
    // (pos G3 PR-P1 que dropou tasks.duration_seconds).
    duration_ms: t.duration_ms ?? null,
    // P1.15 Z3: delta calculado frontend a partir de duration_ms +
    // estimated_duration_seconds (não consumir mais campo backend, que pode
    // não existir canonical). Fallback pra `t.delta_estimated_actual_seconds`
    // só pra back-compat caso backend ainda envie o legado.
    delta_estimated_actual_seconds:
      computeDeltaSeconds(t.duration_ms ?? null, t.estimated_duration_seconds ?? null) ??
      (t.delta_estimated_actual_seconds ?? null),
    retry_count: t.retry_count,
    max_retries: t.max_retries,
    created_at: t.created_at,
    updated_at: t.updated_at,
    started_at: t.started_at ?? null,
    completed_at: t.completed_at ?? null,
    urgency_level: t.urgency_level ?? null,
    estimated_size: t.estimated_size ?? null,
    domain: t.domain ?? null,
  };
}

/** Agrupado por status pra renderização do board. */
export type TasksByStatus = Record<TaskStatus, TaskUI[]>;

export function emptyTasksByStatus(): TasksByStatus {
  return {
    pending: [],
    running: [],
    done: [],
    failed: [],
    retry: [],
    cancelled: [],
    paused: [],
  };
}

/**
 * Estado de filtros — espelhado em URL via useTasksUrlState.
 * `byHandler` mapeia handler (ex: '@agent-runner') → array de target_slugs
 * selecionados pra esse handler. Cada entrada é um dropdown separado na UI.
 */
export interface TaskFilters {
  q: string;
  status: TaskStatus[];
  handler: string[];
  byHandler: Record<string, string[]>;
  donePeriod: '24h' | '7d' | '30d' | 'all';
  /**
   * P1.13 F7 — Quando true, esconde tasks órfãs (parent_task_id NOT NULL +
   * FK canônica NULL). Default false: órfãs aparecem com badge sinalizando
   * o problema, e o admin escolhe esconder se atrapalhar a leitura.
   */
  hideOrphans: boolean;
}

export const DEFAULT_FILTERS: TaskFilters = {
  q: '',
  status: [],
  handler: [],
  byHandler: {},
  donePeriod: '24h',
  hideOrphans: false,
};

export function periodToCutoffMs(period: TaskFilters['donePeriod']): number | null {
  if (period === 'all') return null;
  const HOUR = 60 * 60 * 1000;
  if (period === '24h') return Date.now() - 24 * HOUR;
  if (period === '7d') return Date.now() - 7 * 24 * HOUR;
  if (period === '30d') return Date.now() - 30 * 24 * HOUR;
  return null;
}

export function countByHandlerSelections(byHandler: Record<string, string[]>): number {
  return Object.values(byHandler).reduce((acc, arr) => acc + arr.length, 0);
}
